import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI } from '../services/api';
import OfflineBanner from './OfflineBanner';
import PWAInstallBanner from './PWAInstallBanner';

/* Detect mobile viewport */
const useIsMobile = () => {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
};

/**
 * NAV STRUCTURE
 * Each section has: label, icon, roles (null = all), items[]
 * Each item has: to, label, icon, roles (null = all), badge (optional fn)
 * roles: null = visible to everyone, array = only those roles/sub_roles
 */
const buildNav = (user, isAdmin, unread) => {
  const role = user?.role;
  const subRole = user?.sub_role;
  const isAdminOrSuper = ['super_admin', 'admin'].includes(role);
  const isInternOrSupport = ['Intern', 'Support Member'].includes(subRole);
  const canSeeRevenue = isAdminOrSuper || ['MD', 'SVP', 'RCM', 'Manager'].includes(subRole);
  const canSeeFunnelAnalytics = isAdminOrSuper; // only admin/super_admin see all funnel data
  const canSeeAnalyticsSection = !isInternOrSupport;

  return [
    {
      section: 'MAIN',
      items: [
        { to: '/dashboard',      label: 'Dashboard',      icon: '📊' },
        {
          to: '/notifications',  label: 'Notifications',  icon: '🔔',
          badge: unread > 0 ? (unread > 9 ? '9+' : String(unread)) : null,
        },
      ],
    },
    {
      section: 'SALES',
      items: [
        { to: '/leads',    label: 'Leads',    icon: '🎯' },
        { to: '/students', label: 'Students', icon: '🎓' },
        { to: '/tasks',    label: 'Tasks',    icon: '✅' },
      ],
    },
    {
      section: 'ANALYTICS',
      visible: canSeeAnalyticsSection,
      items: [
        { to: '/reports',          label: 'Reports',         icon: '📋' },
        { to: '/funnel-analytics', label: 'Lead Generation', icon: '📡', visible: canSeeFunnelAnalytics },
      ].filter(i => i.visible !== false),
    },
    {
      section: 'FINANCE',
      items: [
        { to: '/my-earnings', label: 'My Earnings', icon: '📈' },
        { to: '/revenue',     label: 'Revenue',     icon: '💰', visible: canSeeRevenue },
        { to: '/commission',  label: 'Commissions', icon: '💸', visible: isAdminOrSuper },
      ].filter(i => i.visible !== false),
    },
    {
      section: 'TEAM',
      visible: isAdminOrSuper,
      items: [
        { to: '/team',              label: 'Team Members',  icon: '👥' },
        { to: '/admin/objections',  label: 'Objections',    icon: '🛡️' },
        { to: '/funnel-pages',      label: 'Funnel Pages',  icon: '🔧' },
      ],
    },
    {
      section: 'SETTINGS',
      visible: isAdminOrSuper,
      items: [
        { to: '/settings/email',    label: 'Email',          icon: '✉️' },
        { to: '/settings/whatsapp', label: 'WhatsApp',       icon: '📱' },
        { to: '/knowledge',         label: 'Knowledge Base', icon: '🧠' },
      ],
    },
  ].filter(s => s.visible !== false);
};

function NavItem({ item, collapsed }) {
  return (
    <NavLink
      to={item.to}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: collapsed ? '9px 14px' : '8px 12px',
        borderRadius: 7,
        marginBottom: 1,
        textDecoration: 'none',
        transition: 'all 0.15s',
        background: isActive ? 'rgba(201,168,76,0.18)' : 'transparent',
        color: isActive ? '#C9A84C' : 'rgba(255,255,255,0.72)',
        borderLeft: isActive ? '3px solid #C9A84C' : '3px solid transparent',
        fontWeight: isActive ? 600 : 400,
        fontSize: 13.5,
        position: 'relative',
      })}
    >
      <span style={{ fontSize: 16, flexShrink: 0, position: 'relative', lineHeight: 1 }}>
        {item.icon}
        {item.badge && (
          <span style={{
            position: 'absolute', top: -5, right: -8,
            background: '#ef4444', color: '#fff', borderRadius: '50%',
            width: 16, height: 16, fontSize: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
          }}>{item.badge}</span>
        )}
      </span>
      {!collapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>}
    </NavLink>
  );
}

function NavSection({ section, items, collapsed, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const location = useLocation();
  // Auto-open section if a child is active
  const hasActive = items.some(i => location.pathname.startsWith(i.to));

  useEffect(() => {
    if (hasActive) setOpen(true);
  }, [hasActive]);

  return (
    <div style={{ marginBottom: 4 }}>
      {/* Section header */}
      {!collapsed && (
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', padding: '6px 12px 4px',
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'rgba(255,255,255,0.35)', fontSize: 10.5,
            fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}
        >
          <span>{section}</span>
          <span style={{ fontSize: 10, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
        </button>
      )}
      {collapsed && (
        <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '6px 8px' }} />
      )}
      {/* Items */}
      <div style={{
        overflow: 'hidden',
        maxHeight: (open || collapsed) ? 500 : 0,
        transition: 'max-height 0.2s ease',
      }}>
        {items.map(item => (
          <NavItem key={item.to} item={item} collapsed={collapsed} />
        ))}
      </div>
    </div>
  );
}

/* ── Mobile Bottom Nav ───────────────────────────────────────────────────── */
function MobileBottomNav({ unread, onMoreClick }) {
  const location = useLocation();
  const isActive = path => location.pathname.startsWith(path);

  const items = [
    { to: '/dashboard',     label: 'Home',    icon: '📊' },
    { to: '/leads',         label: 'Leads',   icon: '🎯' },
    { to: '/tasks',         label: 'Tasks',   icon: '✅' },
    { to: '/notifications', label: 'Alerts',  icon: '🔔', badge: unread > 0 ? (unread > 9 ? '9+' : String(unread)) : null },
    { label: 'More', icon: '☰', onClick: onMoreClick },
  ];

  return (
    <nav style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000,
      background: '#1B2A6B', borderTop: '1px solid rgba(255,255,255,0.12)',
      display: 'flex', height: 60,
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {items.map((item, i) => {
        const active = item.to ? isActive(item.to) : false;
        const inner = (
          <>
            <span style={{ fontSize: 20, position: 'relative', lineHeight: 1 }}>
              {item.icon}
              {item.badge && (
                <span style={{
                  position: 'absolute', top: -4, right: -8,
                  background: '#ef4444', color: '#fff', borderRadius: '50%',
                  width: 14, height: 14, fontSize: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                }}>{item.badge}</span>
              )}
            </span>
            <span style={{ fontSize: 10, marginTop: 2 }}>{item.label}</span>
          </>
        );

        const commonStyle = {
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 2, textDecoration: 'none', border: 'none',
          background: 'none', cursor: 'pointer', padding: '6px 0',
          color: active ? '#C9A84C' : 'rgba(255,255,255,0.55)',
          fontWeight: active ? 700 : 400,
        };

        if (item.onClick) {
          return <button key={i} style={commonStyle} onClick={item.onClick}>{inner}</button>;
        }
        return <NavLink key={item.to} to={item.to} style={({ isActive: a }) => ({ ...commonStyle, color: a ? '#C9A84C' : 'rgba(255,255,255,0.55)', fontWeight: a ? 700 : 400 })}>{inner}</NavLink>;
      })}
    </nav>
  );
}

/* ── More Drawer (slide-up) ──────────────────────────────────────────────── */
function MoreDrawer({ sections, open, onClose, user, onLogout }) {
  if (!open) return null;
  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1001, background: 'rgba(0,0,0,0.45)' }} />
      {/* Sheet */}
      <div style={{
        position: 'fixed', bottom: 60, left: 0, right: 0, zIndex: 1002,
        background: '#1B2A6B', borderRadius: '20px 20px 0 0',
        padding: '16px 12px 24px', maxHeight: '65vh', overflowY: 'auto',
      }}>
        <div style={{ width: 36, height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2, margin: '0 auto 16px' }} />
        {sections.map(s => (
          <div key={s.section} style={{ marginBottom: 8 }}>
            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', padding: '4px 12px 2px', textTransform: 'uppercase' }}>{s.section}</div>
            {s.items.map(item => (
              <NavLink key={item.to} to={item.to} onClick={onClose} style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                borderRadius: 8, textDecoration: 'none', marginBottom: 1,
                background: isActive ? 'rgba(201,168,76,0.15)' : 'transparent',
                color: isActive ? '#C9A84C' : 'rgba(255,255,255,0.75)',
                fontWeight: isActive ? 600 : 400, fontSize: 14,
              })}>
                <span style={{ fontSize: 18 }}>{item.icon}</span>
                <span>{item.label}</span>
                {item.badge && <span style={{ marginLeft: 'auto', background: '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{item.badge}</span>}
              </NavLink>
            ))}
          </div>
        ))}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: 8, paddingTop: 8 }}>
          <NavLink to="/profile" onClick={onClose} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, textDecoration: 'none', color: 'rgba(255,255,255,0.75)', fontSize: 14 }}>
            <div style={{ width: 28, height: 28, background: '#C9A84C', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#1B2A6B' }}>{user?.name?.[0]?.toUpperCase()}</div>
            <div>
              <div style={{ fontWeight: 600, color: '#fff' }}>{user?.name}</div>
              <div style={{ fontSize: 11, color: '#C9A84C', textTransform: 'capitalize' }}>{user?.sub_role || user?.role?.replace('_', ' ')}</div>
            </div>
          </NavLink>
          <button onClick={onLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', fontSize: 14 }}>
            <span style={{ fontSize: 18 }}>🚪</span><span>Sign Out</span>
          </button>
        </div>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(false);
  const [unread, setUnread] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    const fetchUnread = () => {
      notificationsAPI.getAll().then(res => {
        setUnread(res.data?.data?.unreadCount ?? 0);
      }).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  const sections = buildNav(user, isAdmin, unread);
  const handleLogout = () => { logout(); navigate('/login'); setMoreOpen(false); };

  if (isMobile) {
    return (
      <>
        <OfflineBanner />
        <PWAInstallBanner />
        {/* Mobile compact header */}
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 900,
          background: '#1B2A6B', borderBottom: '1px solid rgba(255,255,255,0.1)',
          height: 52, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 10,
        }}>
          <div style={{ width: 28, height: 28, background: '#C9A84C', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13, color: '#1B2A6B' }}>N</div>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Nova Era Labs</span>
          <span style={{ color: '#C9A84C', fontSize: 11, marginLeft: 2 }}>CRM</span>
        </div>
        {/* Main content */}
        <main style={{ paddingTop: 52, paddingBottom: 60, minHeight: '100vh', background: '#f1f5f9', overflowX: 'hidden' }}>
          <div style={{ padding: '16px 12px' }}>
            <Outlet />
          </div>
        </main>
        <MobileBottomNav unread={unread} onMoreClick={() => setMoreOpen(true)} />
        <MoreDrawer sections={sections} open={moreOpen} onClose={() => setMoreOpen(false)} user={user} onLogout={handleLogout} />
      </>
    );
  }

  return (
    <>
    <OfflineBanner />
    <PWAInstallBanner />
    <div style={{ display: 'flex', height: '100vh', background: '#f1f5f9' }}>
      {/* Sidebar */}
      <aside style={{
        width: collapsed ? 60 : 230,
        background: '#1B2A6B',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.25s ease',
        flexShrink: 0,
        overflow: 'hidden',
      }}>
        {/* Logo */}
        <div style={{
          padding: collapsed ? '16px 12px' : '16px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', gap: 10,
          flexShrink: 0,
        }}>
          <div style={{
            width: 34, height: 34, background: '#C9A84C', borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 15, color: '#1B2A6B', flexShrink: 0,
          }}>N</div>
          {!collapsed && (
            <div style={{ minWidth: 0 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, lineHeight: 1.25, whiteSpace: 'nowrap' }}>Nova Era Labs</div>
              <div style={{ color: '#C9A84C', fontSize: 10.5, whiteSpace: 'nowrap' }}>Partnership CRM</div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(c => !c)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: 16, lineHeight: 1, padding: 2, flexShrink: 0 }}
          >{collapsed ? '›' : '‹'}</button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px 6px', overflowY: 'auto', overflowX: 'hidden' }}>
          {sections.map((s, i) => (
            <NavSection
              key={s.section}
              section={s.section}
              items={s.items}
              collapsed={collapsed}
              defaultOpen={true}
            />
          ))}
        </nav>

        {/* User footer */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', padding: '10px 6px', flexShrink: 0 }}>
          <NavLink
            to="/profile"
            style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 8, textDecoration: 'none', marginBottom: 2 }}
          >
            <div style={{
              width: 30, height: 30, background: '#C9A84C', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 13, color: '#1B2A6B', flexShrink: 0,
            }}>
              {user?.name?.[0]?.toUpperCase()}
            </div>
            {!collapsed && (
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
                <div style={{ fontSize: 10.5, color: '#C9A84C', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{user?.sub_role || user?.role?.replace('_', ' ')}</div>
              </div>
            )}
          </NavLink>
          <button
            onClick={handleLogout}
            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px', borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', fontSize: 13 }}
          >
            <span style={{ fontSize: 15, flexShrink: 0 }}>🚪</span>
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, padding: 24 }}>
          <Outlet />
        </div>
      </main>
    </div>
    </>
  );
}
